import _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiConfigs } from '../../../api/APIConfigs';
import { BibleStudy } from '../../../api/models/BibleStudy';
import { CreateBibleStudySection } from '../../../api/models/BibleStudySection';
import { UploadResult } from '../../../api/models/UploadResult';
import { routes } from '../../../route-list';
import {
  generateModuleFormState,
  getModuleRequestBodyLibrary,
  moduleUploadLib,
} from '../../../util/bible-study-module';
import { convertHoursToMinutes, convertMinutesToHours } from '../../../util/convert-time';
import { generateFileFromPlaybackId } from '../../../util/mux-utilities';
import { useAsyncRequest } from '../../../util/use-async-request';
import { useRequest } from '../../../util/use-request';
import { isDuplicated, validateFileSize, validateModuleFormState } from '../../../util/validator';
import { RhConfirmModal } from '../../core/RhConfirmModal';
import { BibleStudyFormState, ModuleFormState } from '../bible-study-form';
import { generateFileInputKeys, getDayOptions, getModuleArrayIndex } from '../bible-study-form.helper';
import { BibleStudyFormScreen } from '../components/BibleStudyFormScreen';
import { initialModuleFormState, initialStudyFormState } from './BibleStudyFormContainer';

export const EditBibleStudyFormContainer = () => {
  // This state holds the original form values. This should only be set once.
  const [alwaysPristineFormState, setAlwaysPristineFormState] = useState<BibleStudyFormState>(initialStudyFormState);
  const [form, setForm] = useState<BibleStudyFormState>(initialStudyFormState);
  const [moduleForm, setModuleForm] = useState<ModuleFormState>(initialModuleFormState);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isModuleModalOpen, setIsModuleModalOpen] = useState(false);
  const [sections, setSections] = useState<CreateBibleStudySection[]>([]);
  // state for file-related errors
  const [moduleFormError, setModuleFormError] = useState<string | null>(null);
  const [isModuleFormFileUploading, setIsModuleFormFileUploading] = useState(false);
  // we need keys to clear out input[file] value
  const [fileInputKeys, setFileInputKeys] = useState(generateFileInputKeys());
  const [isModuleFormLoading, setIsModuleFormLoading] = useState(false);
  const [isDeletingStudy, setIsDeletingStudy] = useState(false);
  const [isContentFormModalOpen, setIsContentFormModalOpen] = useState(false);
  const [isGuidedModalOpen, setIsGuidedModalOpen] = useState(false);

  const history = useHistory();
  const request = useRequest();
  const { id: programId } = useParams<{ id: string }>();

  // load bible study on mount
  const { loading: isLoadingStudy, data: originalStudy } = useAsyncRequest(
    useCallback(() => {
      return request<BibleStudy>(apiConfigs.getBibleStudy(programId));
    }, [programId, request]),
    useCallback(async result => {
      const study: BibleStudy = result;

      let introVideo: File | undefined = undefined;
      if (study.introMuxId) {
        try {
          introVideo = await generateFileFromPlaybackId(study.introMuxId);
        } catch (e: any) {
          toast.error('Failed to load Introduction Video');
        }
      }

      const { hours, minutes } = convertMinutesToHours(study.duration || 0);

      const formState: BibleStudyFormState = {
        ...initialStudyFormState,
        ...study,
        featureImageUrl: study.imageUrl,
        introVideo,
        minutes,
        hours,
      };

      setAlwaysPristineFormState(formState);
      setForm(formState);
      setSections(study.sections);
    }, [])
  );

  return (
    <BibleStudyFormScreen
      mode="edit"
      form={form}
      moduleForm={moduleForm}
      isSaving={isSaving || isLoadingStudy || isDeletingStudy}
      isEditingTitle={isEditingTitle}
      isModuleModalOpen={isModuleModalOpen}
      sections={sections}
      moduleFormError={moduleFormError}
      isModuleFormFileUploading={isModuleFormFileUploading}
      fileInputKeys={fileInputKeys}
      isModuleFormLoading={isModuleFormLoading}
      isContentFormModalOpen={isContentFormModalOpen}
      weekTagOptions={form.contentMaps.map(item => ({ label: `Week ${item.week}`, value: `Week ${item.week}` }))}
      dayTagOptions={getDayOptions(form.contentMaps, moduleForm.weekTag)}
      isGuidedModalOpen={isGuidedModalOpen}
      userClickedCancelonGuidedModal={() => setIsGuidedModalOpen(false)}
      userClickedSubmitonGuidedModal={contentMaps => {
        setForm(prevState => ({ ...prevState, contentMaps: contentMaps }));
        setIsGuidedModalOpen(false);
      }}
      userClickedEditContentMap={() => setIsGuidedModalOpen(true)}
      userClickedAddContentViaModal={() => setIsContentFormModalOpen(prev => !prev)}
      userUpdatedForm={setForm}
      userUpdatedModuleForm={setModuleForm}
      userClickedEditTitle={() => {
        setIsEditingTitle(!isEditingTitle);
      }}
      userClickedSave={async () => {
        // additional validation, if user enables button on inspect elements
        if (!form.title || !form.description) {
          toast.error('Study must have a title and description before saving.');
          return;
        }

        if (form.contentMaps == null || form.contentMaps.length == 0) {
          toast.error('Study must set content map');
          return;
        }

        if (!originalStudy) {
          toast.error('Bible Study was not loaded properly');
          return;
        }

        try {
          setIsSaving(true);
          let imageUrl = originalStudy.imageUrl;

          // if there is a {form.featureImage},
          // that means the user selected a new one
          if (form.featureImage) {
            const uploadResult = await request<UploadResult>(apiConfigs.uploadImage(form.featureImage));
            imageUrl = uploadResult.filePath;
          }

          // upload video to the server
          let introVideoMuxId = originalStudy.introMuxId;
          if (form.introVideo && !form.introVideo.name.startsWith('https://stream.mux.com')) {
            const uploaded = await request<UploadResult>(apiConfigs.uploadVideo(form.introVideo));
            introVideoMuxId = uploaded.muxID;
          }

          // update bible study
          await request(
            apiConfigs.putBibleStudy({
              ...originalStudy,
              title: form.title,
              description: form.description,
              tags: form.tags,
              imageUrl,
              // Populate tag with name if it's empty
              sections: sections.map(section => (!section.tag ? { ...section, tag: section.name } : section)),
              introMuxId: introVideoMuxId,
              publishingStatus: form.publishingStatus || 'published',
              duration: convertHoursToMinutes(form.hours, form.minutes),
              contentMaps: form.contentMaps,
              categories: form.categories,
            })
          );

          toast.success('Study updated!');
          setIsSaving(false);

          history.push(routes.BIBLE_STUDIES);
        } catch (e: any) {
          toast.error(e.message || 'Failed to update study');
          setIsSaving(false);
        }
      }}
      userPushedToTags={() => {
        // validate tags for duplicates
        if (isDuplicated(form.tags, form.tag)) {
          toast.error(`Duplicate tag "${form.tag}"`);
          return;
        }
        const newTags = [...form.tags, form.tag];
        setForm(f => ({ ...f, tags: newTags, tag: '' }));
      }}
      userToggledModuleModal={() => {
        setIsModuleModalOpen(!isModuleModalOpen);
      }}
      userResetModuleForm={() => {
        setModuleForm(initialModuleFormState);
        setModuleFormError(null);
        setFileInputKeys(generateFileInputKeys());
      }}
      userSubmittedModuleForm={() => {
        // form validation
        const formError = validateModuleFormState(moduleForm);
        if (formError || moduleFormError) {
          toast.error(formError || 'Module form has some errors');
          return;
        }

        // here, we don't need to handle file upload. it's already
        // handled by the [userChoseModuleFile] prop

        // IDENTIFYING MODULE FORM MODE (edit/add)
        // we need to identify if the current form is being used for
        // editing a module or adding a module to an existing section
        //
        // we do this by checking if there is a passed [moduleIndex]
        // which only modules that are currently being edited have

        const newSections: CreateBibleStudySection[] = sections.map((section, i) => {
          // [getModuleFormLibrary] abstracts generation of module format for each type
          const newModule = getModuleRequestBodyLibrary(moduleForm)[moduleForm.type!];

          const editedModules = Array.from(section.modules);
          editedModules.splice(moduleForm.moduleIndex!, 1, newModule);

          const newModules = [...section.modules, newModule];

          return moduleForm.sectionIndex === i
            ? { ...section, modules: !_.isNil(moduleForm.moduleIndex) ? editedModules : newModules }
            : section;
        });

        setSections(newSections);
        setModuleForm(initialModuleFormState);
        setIsModuleModalOpen(false);
      }}
      userClickedSaveViaContentModal={() => {
        // Perform form validation
        const formError = validateModuleFormState(moduleForm);
        if (formError || moduleFormError) {
          toast.error(formError || 'Module form has some errors');
          return;
        }

        // Generate module from form
        const newModule = getModuleRequestBodyLibrary(moduleForm)[moduleForm.type!];

        // Check if section tag already exists
        const sectionIndex = sections.findIndex(
          section => section.name.toLowerCase() === moduleForm.weekTag.toLowerCase()
        );

        const isSectionFound = sectionIndex !== -1;
        if (isSectionFound) {
          // If section exists, push content to that section
          const newSections = [...sections];
          const selectedSection = sections[sectionIndex];
          const newModules = [...selectedSection.modules];

          const isCurrentlyEditing = !_.isNil(moduleForm.moduleIndex);
          if (isCurrentlyEditing) {
            const moduleIndex = moduleForm.moduleIndex!;
            newModules.splice(moduleIndex, 1, { ...newModule });
          } else {
            newModules.push(newModule);
          }

          newSections.splice(sectionIndex, 1, { ...selectedSection, modules: newModules });
          setSections(newSections);
        } else {
          // If it doesnt exist yet, create new section and add tag to [weekTags]
          const newSection = {
            name: moduleForm.weekTag,
            modules: [newModule],
            tag: moduleForm.weekTag,
          };

          setSections(prev => [...prev, newSection]);
        }

        // Reset form
        setModuleForm(initialModuleFormState);
        // Close form modal
        setIsContentFormModalOpen(false);
      }}
      userClickedCancel={() => {
        // Check first if the form is pristine. If pristine, proceed without confirmation.
        if (_.isEqual(form, alwaysPristineFormState)) {
          history.push(routes.BIBLE_STUDIES__VIEW(programId));
        } else {
          confirmAlert({
            customUI: options => (
              <RhConfirmModal
                description="Are you sure you want to exit without saving?"
                isOpen={true}
                onClose={options.onClose}
                onOk={() => {
                  options.onClose();
                  history.push(routes.BIBLE_STUDIES__VIEW(programId));
                }}
              />
            ),
          });
        }
      }}
      userUpdatedSections={setSections}
      userChoseModuleFile={async e => {
        setModuleFormError(null);

        if (!e.target.files?.length) return;
        const file = e.target.files[0];

        const fileSizeError = validateFileSize(file);
        if (fileSizeError) {
          toast.error(fileSizeError);
          return;
        }

        setModuleForm(prev => ({ ...prev, data: file, fileName: file.name }));

        try {
          setIsModuleFormFileUploading(true);
          const src = await moduleUploadLib[moduleForm.type!](request, file);
          setModuleForm(prev => ({ ...prev, url: src }));
        } catch (e: any) {
          toast.error(e.message || 'Failed to upload file');
          setModuleFormError(`Failed to upload ${file.name}`);
        } finally {
          setIsModuleFormFileUploading(false);
        }
      }}
      userClickedDeleteModuleFormFile={() => {
        setModuleForm(prev => ({ ...prev, data: null, url: null, fileName: null }));
        setModuleFormError(null);
        setFileInputKeys(generateFileInputKeys());
      }}
      userClickedEditModule={(weekTag, dayTag, moduleDayIndex) => {
        try {
          setIsContentFormModalOpen(true);
          setIsModuleFormLoading(true);

          const moduleIndex = getModuleArrayIndex(sections, { weekTag, dayTag, moduleDayIndex });
          const sectionIndex = sections.findIndex(
            section => (section.tag || section.name).toLowerCase() === weekTag.toLowerCase()
          );
          const newModuleFormState = generateModuleFormState({ sections, sectionIndex, moduleIndex });
          setModuleForm(previousFormState => ({
            ...previousFormState,
            ...newModuleFormState,
          }));

          setIsModuleFormLoading(false);
        } catch (e: any) {
          setIsModuleFormLoading(false);
          setIsContentFormModalOpen(true);

          toast.error(e.message || 'Failed to load module');
        }
      }}
      userClickedDeleteModule={(weekTag, dayTag, moduleDayIndex) => {
        confirmAlert({
          customUI: options => (
            <RhConfirmModal
              isOpen={true}
              description="Are you sure you want to delete this module?"
              onClose={options.onClose}
              onOk={() => {
                const moduleIndex = getModuleArrayIndex(sections, { weekTag, dayTag, moduleDayIndex });
                const sectionIndex = sections.findIndex(
                  section => (section.tag || section.name).toLowerCase() === weekTag.toLowerCase()
                );

                // Remove module from section
                const newSections: CreateBibleStudySection[] = sections.map((section, i) => {
                  const modules = Array.from(section.modules).filter((_, i) => i !== moduleIndex);
                  return sectionIndex === i ? { ...section, modules } : section;
                });

                setSections(newSections);
                options.onClose();
              }}
            />
          ),
        });
      }}
      userClickedDeleteSection={sectionIndex => {
        confirmAlert({
          customUI: options => (
            <RhConfirmModal
              isOpen={true}
              description="Are you sure you want to delete this section?"
              onClose={options.onClose}
              onOk={() => {
                // remove a section
                const newSections: CreateBibleStudySection[] = Array.from(sections)
                  .filter((_, i) => i !== sectionIndex)
                  .map((section, i) => ({ ...section }));
                setSections(newSections);
                options.onClose();
              }}
            />
          ),
        });
      }}
      userClickedDeleteStudy={() => {
        confirmAlert({
          customUI: options => (
            <RhConfirmModal
              isOpen={true}
              description="Are you sure you want to delete this study?"
              onClose={options.onClose}
              onOk={async () => {
                options.onClose();
                try {
                  setIsDeletingStudy(true);
                  await request(apiConfigs.deleteProgram(programId));
                  setIsDeletingStudy(false);
                  toast.success('Study deleted!');
                  history.replace(routes.BIBLE_STUDIES);
                } catch (e: any) {
                  setIsDeletingStudy(false);
                  toast.error(e.message || 'Failed to delete study');
                }
              }}
            />
          ),
        });
      }}
    />
  );
};
